'use client';
import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import MultiRangeSlider from '../MultiRangeSlider/MultiRangeSlider';
import SingleRangeSlider from '../SingleRangeSlider';

import s from './MapDateRangeFilter.module.scss';

// The sliders trade in whole days since the extent opened, so their positions
// lie where the dates do in time. YYYY-MM-DD parses as UTC midnight, which
// makes the day arithmetic exact.
const DAY_MS = 24 * 60 * 60 * 1000;

const dayOffset = (date, origin) =>
    Math.round((Date.parse(date) - Date.parse(origin)) / DAY_MS);

const dateAt = (offset, origin) =>
    new Date(Date.parse(origin) + offset * DAY_MS).toISOString().slice(0, 10);

// What a whole date reads as. The round trip through Date throws out the
// well-formed impossibles: a 31st of February comes back as a March date
// rather than the one asked for. (Date.parse quietly does that same rollover,
// which is why it is no test.)
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const isDate = (text) => {
    if (!ISO_DATE.test(text)) {
        return false;
    }

    const [year, month, day] = text.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

/**
 * One end of the window. The visible field is a text one so the date reads
 * and types as YYYY-MM-DD for everyone — a native date field dresses it in
 * whatever the browser's locale wears. The native field is still here, hidden,
 * purely as the way to its calendar dialog.
 */
const DateField = ({ label, pickerLabel, value, min, max, onCommit }) => {
    const inputId = useId();
    const pickerRef = useRef(null);
    const [draft, setDraft] = useState(value);

    // A commit from anywhere — this field, its calendar, the other end
    // narrowing this one — is the new text.
    useEffect(() => {
        setDraft(value);
    }, [value]);

    const type = (text) => {
        setDraft(text);
        onCommit(text);
    };

    return (
        <div className={s.Field}>
            <label className={s.Label} htmlFor={inputId}>
                {label}
            </label>
            <div className={s.InputRow}>
                <input
                    id={inputId}
                    className={s.Input}
                    type="text"
                    maxLength={10}
                    placeholder="YYYY-MM-DD"
                    value={draft}
                    onChange={(event) => type(event.target.value)}
                    // Whatever a walked-away-from edit left behind, the field
                    // goes back to reading as the window actually shown.
                    onBlur={() => setDraft(value)}
                />
                <button
                    type="button"
                    className={s.PickerButton}
                    aria-label={`${label}: ${pickerLabel}`}
                    onClick={() => pickerRef.current?.showPicker?.()}
                />
                <input
                    ref={pickerRef}
                    className={s.Picker}
                    type="date"
                    tabIndex={-1}
                    aria-hidden="true"
                    value={value}
                    // The data's own extent, and the other end of the window:
                    // all the calendar can offer is dates the map can show.
                    min={min}
                    max={max}
                    onChange={(event) => onCommit(event.target.value)}
                />
            </div>
        </div>
    );
};

DateField.propTypes = {
    label: PropTypes.string.isRequired,
    pickerLabel: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    min: PropTypes.string.isRequired,
    max: PropTypes.string.isRequired,
    onCommit: PropTypes.func.isRequired,
};

/**
 * The switch between the two shapes a window of time can take. Not a checkbox:
 * it does not turn anything on, it swaps one control for the other, and it says
 * which one it swaps to.
 */
const ModeSwitch = ({ isOn, label, onToggle }) => (
    <div className={s.Mode}>
        <button
            type="button"
            role="switch"
            aria-checked={isOn}
            className={s.Switch}
            onClick={onToggle}>
            <span className={s.SwitchKnob} />
        </button>
        <span className={s.ModeLabel}>{label}</span>
    </div>
);

ModeSwitch.propTypes = {
    isOn: PropTypes.bool.isRequired,
    label: PropTypes.string.isRequired,
    onToggle: PropTypes.func.isRequired,
};

/**
 * A window of time for narrowing a map, in either of two shapes: a range with
 * both ends, or a single date. One switch turns one into the other, so there is
 * one control on screen rather than two ways to say the same thing.
 *
 * Each shape is a field and a slider over the same value: the field is how a
 * reader states a date, the slider how they feel for one. The slider carries no
 * labels of its own — the fields above it already read the dates.
 *
 * Dates in, out and on screen are all `YYYY-MM-DD`.
 */
const MapDateRangeFilter = ({
    min,
    max,
    value,
    isNarrowed = false,
    onChange,
    fromLabel,
    toLabel,
    dateLabel,
    pickerLabel,
    rangeModeLabel,
    dateModeLabel,
}) => {
    // A day, not a window: what the reader last dragged the single handle to.
    const [isSingleDate, setIsSingleDate] = useState(false);

    // Clearing the filters puts the window back to the whole extent, and a
    // single date is not that — so the control goes back to the shape it
    // started in rather than sitting on a date the map is no longer narrowed
    // to.
    useEffect(() => {
        if (!isNarrowed) {
            setIsSingleDate(false);
        }
    }, [isNarrowed]);
    // A single day is not a range. With nothing to narrow, the fields would be
    // chrome over a decision a reader cannot make.
    if (!(max > min)) {
        return null;
    }

    // A half-typed date is not a window, a field cleared on the way to a new
    // date is not a request to see everything, and a window outside the data
    // shows the same map with dates that imply otherwise — so nothing commits
    // until the whole window reads as dates inside the data, in order.
    const change = (next) => {
        if (!isDate(next.from) || !isDate(next.to) || next.from > next.to) {
            return;
        }

        if (next.from < min || next.to > max) {
            return;
        }

        onChange(next);
    };

    const span = dayOffset(max, min);
    const asDate = (offset) => dateAt(offset, min);

    // Switching shape reshapes the window around the date already shown, so the
    // map answers on the spot rather than on the next drag. A single date is
    // that day alone; opening it back up leaves that day as the window's end
    // and reaches back to the start of the data.
    const switchMode = () => {
        const next = !isSingleDate;

        setIsSingleDate(next);
        onChange(
            next
                ? { from: value.to, to: value.to }
                : { from: min, to: value.to }
        );
    };

    if (isSingleDate) {
        return (
            <div className={s.Root}>
                <div className={s.Control}>
                    <DateField
                        label={dateLabel}
                        pickerLabel={pickerLabel}
                        value={value.to}
                        min={min}
                        max={max}
                        onCommit={(date) => change({ from: date, to: date })}
                    />
                    <SingleRangeSlider
                        min={0}
                        max={span}
                        value={dayOffset(value.to, min)}
                        showLabels={false}
                        onChange={(offset) =>
                            change({
                                from: asDate(offset),
                                to: asDate(offset),
                            })
                        }
                    />
                </div>

                <ModeSwitch isOn label={rangeModeLabel} onToggle={switchMode} />
            </div>
        );
    }

    return (
        <div className={s.Root}>
            <div className={s.Control}>
                <div className={s.Fields}>
                    <DateField
                        label={fromLabel}
                        pickerLabel={pickerLabel}
                        value={value.from}
                        min={min}
                        max={value.to}
                        onCommit={(from) => change({ from, to: value.to })}
                    />
                    <DateField
                        label={toLabel}
                        pickerLabel={pickerLabel}
                        value={value.to}
                        min={value.from}
                        max={max}
                        onCommit={(to) => change({ from: value.from, to })}
                    />
                </div>

                <MultiRangeSlider
                    min={0}
                    max={span}
                    minVal={dayOffset(value.from, min)}
                    maxVal={dayOffset(value.to, min)}
                    showValues={false}
                    onChange={({ min: from, max: to }) =>
                        change({ from: asDate(from), to: asDate(to) })
                    }
                />
            </div>

            <ModeSwitch
                isOn={false}
                label={dateModeLabel}
                onToggle={switchMode}
            />
        </div>
    );
};

MapDateRangeFilter.propTypes = {
    // The extent the data covers, as YYYY-MM-DD.
    min: PropTypes.string.isRequired,
    max: PropTypes.string.isRequired,
    // The window currently selected, which starts as the whole extent.
    value: PropTypes.shape({
        from: PropTypes.string.isRequired,
        to: PropTypes.string.isRequired,
    }).isRequired,
    // Whether that window narrows anything. False again once the filters are
    // cleared, which is when the control returns to its range shape.
    isNarrowed: PropTypes.bool,
    // Called with { from, to } once both read as dates and are in order.
    onChange: PropTypes.func.isRequired,
    fromLabel: PropTypes.string.isRequired,
    toLabel: PropTypes.string.isRequired,
    // Names the one field the single-date shape has.
    dateLabel: PropTypes.string.isRequired,
    // Names the calendar button; each button pairs it with its field's label.
    pickerLabel: PropTypes.string.isRequired,
    // What the switch offers, in each shape: the other one.
    rangeModeLabel: PropTypes.string.isRequired,
    dateModeLabel: PropTypes.string.isRequired,
};

export default MapDateRangeFilter;
